/*  ========================================================================
    # Sections - Projects
    ========================================================================  */

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useTranslation from '../../hooks/useTranslation';
import useLocalization from '../../hooks/useLocalization';
import bem from '../../helpers/bem';

// Formats
const GermanEUR = new Intl.NumberFormat('de-DE', {
  style: 'currency',
  currency: 'EUR',
  maximumFractionDigits: 0
});

const ProductConfigurator = ({
  productContent,
  productVariants,
  modules,
  ninjaformsFieldId,
  totalRemarks
}) => {
  const __ = useTranslation();

  // Icons
  const local = useLocalization();
  const iconPlus = `${local('svgSprite')}#plus`;
  const iconCheck = `${local('svgSprite')}#check`;

  // Configuration state
  const [variantIndex, setVariantIndex] = useState(0);
  const onVariantChange = e => setVariantIndex(parseInt(e.target.value, 10));
  const [selectedModules, setSelectedModules] = useState([]);

  const toggleModule = moduleName => {
    setSelectedModules(oldModules =>
      oldModules.includes(moduleName)
        ? [...oldModules].filter(m => m !== moduleName)
        : [...oldModules, moduleName]
    );
  };

  // Formatted values
  const variantName = useMemo(() => productVariants[variantIndex].name, [variantIndex]);
  const variantPrice = useMemo(() => productVariants[variantIndex].price, [variantIndex]);
  const totalPrice = useMemo(() => {
    let moduleSum = 0;
    selectedModules.forEach(
      moduleName => (moduleSum += modules.filter(m => m.name === moduleName)[0]?.price)
    );

    return productVariants[variantIndex].price + moduleSum;
  }, [variantIndex, selectedModules]);

  // Update logic for the form field
  const updateFormField = () => {
    let fieldValue = `${variantName} (${GermanEUR.format(variantPrice)})`;
    selectedModules.forEach(moduleName => {
      const module = modules.filter(m => m.name === moduleName)[0];
      if (!module) return;
      fieldValue += `\n + ${module.name} (${GermanEUR.format(module.price)})`;
    });
    jQuery(`#nf-field-${ninjaformsFieldId}`).val(fieldValue).trigger('change');
  };

  // Run on every input change
  useEffect(updateFormField, [variantIndex, selectedModules]);

  // Run once the NinjaForms form is initialized (https://stackoverflow.com/a/51705069)
  useEffect(() => {
    if (typeof Marionette !== 'undefined') {
      const myController = Marionette.Object.extend({
        initialize() {
          this.listenTo(nfRadio.channel('form'), 'render:view', updateFormField);
        }
      });
      new myController();
    }
  }, []);

  return (
    <div className={bem('s-product-configurator', 'wrapper')}>
      <div className='container'>
        <div
          className={bem('s-product-configurator', 'wizard', {
            'has-modules': modules?.length > 0
          })}
        >
          <div className={bem('s-product-configurator', 'product')}>
            <div className={bem('s-product-configurator', 'card', { active: true })}>
              <div
                className={bem('s-product-configurator', 'product-content', {}, ['wysiwyg-block'])}
                dangerouslySetInnerHTML={{ __html: productContent }}
              ></div>
              <div className={bem('s-product-configurator', 'variant-selector')}>
                <div className={bem('s-product-configurator', 'variants')}>
                  {productVariants.map((variant, index) => (
                    <div
                      className={bem('s-product-configurator', 'variant', {}, ['h5'])}
                      key={index}
                    >
                      <input
                        type='radio'
                        name='variant'
                        id={`variant_${index}`}
                        value={index}
                        checked={variantIndex === index}
                        onChange={onVariantChange}
                      />
                      <label htmlFor={`variant_${index}`}>{variant.name}</label>
                    </div>
                  ))}
                </div>
                <div className={bem('s-product-configurator', 'variant-price', {}, ['h4'])}>
                  {GermanEUR.format(variantPrice)}
                </div>
              </div>
            </div>
          </div>
          <div className={bem('s-product-configurator', 'modules')}>
            {modules.map(module => {
              const selected = !!selectedModules.includes(module.name);
              return (
                <div
                  key={module.name}
                  className={bem(
                    's-product-configurator',
                    'module',
                    {
                      selected
                    },
                    ['s-product-configurator__card']
                  )}
                  onClick={() => toggleModule(module.name)}
                  title={
                    selected
                      ? __('section-product-configurator-module-remove')
                      : __('section-product-configurator-module-add')
                  }
                >
                  <div
                    className={bem('s-product-configurator', 'module-badge', {
                      selected
                    })}
                  >
                    <svg
                      viewBox='0 0 200 200'
                      className={bem('s-product-configurator', 'module-badge-icon', {
                        active: !selected
                      })}
                    >
                      <use href={iconPlus} />
                    </svg>
                    <svg
                      viewBox='0 0 200 200'
                      className={bem('s-product-configurator', 'module-badge-icon', {
                        active: selected
                      })}
                    >
                      <use href={iconCheck} />
                    </svg>
                  </div>
                  <div
                    className={bem('s-product-configurator', 'module-content', {}, [
                      'wysiwyg-block'
                    ])}
                    dangerouslySetInnerHTML={{ __html: module.content }}
                  />
                  <div
                    className={bem('s-product-configurator', 'module-select', {
                      selected
                    })}
                  >
                    <label className='h5'>
                      {selected
                        ? __('section-product-configurator-selected')
                        : __('section-product-configurator-unselected')}
                    </label>
                    <div className={bem('s-product-configurator', 'module-price', {}, ['h4'])}>
                      + {GermanEUR.format(module.price)}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        <div className={bem('s-product-configurator', 'footer')}>
          <div className={bem('s-product-configurator', 'total', {}, ['h4'])}>
            {__('section-product-configurator-total')}:
            <span className={bem('s-product-configurator', 'total-value')}>
              {GermanEUR.format(totalPrice)}
            </span>
          </div>
          <div className={bem('s-product-configurator', 'total-remarks')}>{totalRemarks}</div>
        </div>
      </div>
    </div>
  );
};

ProductConfigurator.propTypes = {
  productContent: PropTypes.string,
  productVariants: PropTypes.array,
  modules: PropTypes.array,
  ninjaformsFieldId: PropTypes.number,
  totalRemarks: PropTypes.string
};

export default ProductConfigurator;
